import { createContext, ReactNode, useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useBranchDetailsQuery } from '../../queries';
import { BomSettingsInterface, IQuoteAttachmentDocumentsPreset, OMTemplateInterface, QuoteTemplateInterface, SubmittalTemplateInterface, BranchDetailsReponse } from '../../queries/apiTypes';
import { BranchBrandingInterface, initialCompanyDetailsForAddLocationInterface } from '../../utils/helperTypes';
import { BranchDetailsData, BranchDetailsErrorState } from '../BranchDetails/BranchDetails.types';
import {
    initialBomSettingState,
    initialBranchBrandingState,
    initialBranchDetailsErrorState,
    initialBranchDetailsState,
    initialCompanyDetails,
    initialOMPackageDetails,
    initialQuoteDetails,
    initialSubmittalDetails
} from '../../utils/helper';
import { AxiosResponse } from 'axios';


interface IBranchLocationsRef {
    branchDetailsTemplate?: BranchDetailsData;
    branchBrandingTemplate?: BranchBrandingInterface;
    quoteTemplate?: QuoteTemplateInterface;
    submittalTemplate?: SubmittalTemplateInterface;
    omPackageTemplate?: OMTemplateInterface;
    bomSettingTemplate?: BomSettingsInterface;
}
interface IContext {
    currentLocationId: number,
    isDuplicate: boolean,
    companyDetailsLoading: boolean,
    branchDetailsTemplate: BranchDetailsData,
    setBranchDetailsTemplate: React.Dispatch<React.SetStateAction<BranchDetailsData>>,
    branchDetailsErrorState: BranchDetailsErrorState,
    setBranchDetailsErrorState: React.Dispatch<React.SetStateAction<BranchDetailsErrorState>>,
    branchBrandingTemplate: BranchBrandingInterface,
    setBranchBrandingTemplate: React.Dispatch<React.SetStateAction<BranchBrandingInterface>>,
    quoteTemplate: QuoteTemplateInterface,
    setQuoteTemplate: React.Dispatch<React.SetStateAction<QuoteTemplateInterface>>,
    submittalTemplate: SubmittalTemplateInterface,
    setSubmittalTemplate: React.Dispatch<React.SetStateAction<SubmittalTemplateInterface>>,
    omPackageTemplate: OMTemplateInterface,
    setOmPackageTemplate: React.Dispatch<React.SetStateAction<OMTemplateInterface>>,
    bomSettingTemplate: BomSettingsInterface,
    setBomSettingTemplate: React.Dispatch<React.SetStateAction<BomSettingsInterface>>,
    branchLocationsRef: React.MutableRefObject<IBranchLocationsRef>,
    companyDetails: initialCompanyDetailsForAddLocationInterface,
    companyData: AxiosResponse<BranchDetailsReponse, any> | undefined
}




export const AddEditLocationContext = createContext<IContext>({
    currentLocationId: 0,
    isDuplicate: false,
    companyDetailsLoading: false,
    branchDetailsTemplate: initialBranchDetailsState,
    setBranchDetailsTemplate: () => { },
    branchDetailsErrorState: initialBranchDetailsErrorState,
    setBranchDetailsErrorState: () => { },
    branchBrandingTemplate: initialBranchBrandingState,
    setBranchBrandingTemplate: () => { },
    quoteTemplate: initialQuoteDetails,
    setQuoteTemplate: () => { },
    submittalTemplate: initialSubmittalDetails,
    setSubmittalTemplate: () => { },
    omPackageTemplate: initialOMPackageDetails,
    setOmPackageTemplate: () => { },
    bomSettingTemplate: initialBomSettingState,
    setBomSettingTemplate: () => { },
    branchLocationsRef: { current: {} },
    companyDetails: initialCompanyDetails,
    companyData: {} as AxiosResponse<BranchDetailsReponse, any> | undefined

});

export const AddEditLocContextProvider = ({ children }: { children: ReactNode }) => {

    const navigate = useNavigate();
    const { locationId } = useParams();
    const [searchParams] = useSearchParams();

    const isDuplicate = Boolean(searchParams.get('source'))
    const currentLocationId = locationId ? Number(locationId) : isDuplicate ? Number(searchParams.get('source')) : 0

    const { data: companyData, isLoading, error } = useBranchDetailsQuery(Number(currentLocationId), { enabled: Boolean(currentLocationId && Number(currentLocationId) !== 0) });

    const [companyDetails, setCompanyDetails] = useState<initialCompanyDetailsForAddLocationInterface>(initialCompanyDetails);

    const [branchDetailsTemplate, setBranchDetailsTemplate] = useState<BranchDetailsData>(initialBranchDetailsState);
    const [branchDetailsErrorState, setBranchDetailsErrorState] = useState<BranchDetailsErrorState>(initialBranchDetailsErrorState);

    const [branchBrandingTemplate, setBranchBrandingTemplate] = useState<BranchBrandingInterface>(initialBranchBrandingState);
    const [quoteTemplate, setQuoteTemplate] = useState<QuoteTemplateInterface>(initialQuoteDetails);
    const [submittalTemplate, setSubmittalTemplate] = useState<SubmittalTemplateInterface>(initialSubmittalDetails);
    const [omPackageTemplate, setOmPackageTemplate] = useState<OMTemplateInterface>(initialOMPackageDetails);
    const [bomSettingTemplate, setBomSettingTemplate] = useState<BomSettingsInterface>(initialBomSettingState);


    const branchLocationsRef = useRef<IBranchLocationsRef>({});

    useEffect(() => {
        if (currentLocationId && ((error as any)?.response?.status === 403 || (error as any)?.response?.status === 404)) {
            navigate(`/v2/settings/company-locations`);
        }
    }, [error, currentLocationId]);

    useEffect(() => {
        if (locationId && companyData?.data && !isDuplicate) {
            // For EDIT location
            const editLocData = {
                ...companyData?.data,
                logo: companyData?.data?.logo || '',
                picture: companyData?.data?.picture || '',
                erpId: companyData?.data?.erp_id || ''
            } as initialCompanyDetailsForAddLocationInterface['data'];
            setCompanyDetails(() => ({ data: editLocData }));
        } else if (currentLocationId && isDuplicate && companyData?.data) {
            // For DUPLICATE location
            const duplicateState = {
                ...initialBranchDetailsState,
                logo: companyData?.data?.logo ? (companyData?.data?.logo) : '',
                picture: companyData?.data?.picture ? (companyData?.data?.picture) : '',
                submittal: companyData?.data?.submittal,
                o_m: companyData?.data?.o_m,
                quote: companyData?.data?.quote,
                bom: companyData?.data?.bom,
            };
            setCompanyDetails(() => ({ data: duplicateState }));
        }

    }, [companyData?.data, currentLocationId]);

    useEffect(() => {
        if (!companyDetails?.data) {
            return;
        }

        const currentBranchDetailsTemplate = {
            ...branchDetailsTemplate,
            name: companyDetails?.data.name,
            email: companyDetails?.data.email,
            phone1: companyDetails?.data.phone1,
            website: companyDetails?.data.website || '',
            address1: companyDetails?.data.address1 || '',
            address2: companyDetails?.data.address2 || '',
            erpId: companyDetails?.data.erpId || '',
            city: companyDetails?.data.city || '',
            country: companyDetails?.data.country || '',
            state: companyDetails?.data.state || '',
            zipcode: companyDetails?.data.zipcode || ''
        }
        setBranchDetailsTemplate(currentBranchDetailsTemplate);
        branchLocationsRef.current['branchDetailsTemplate'] = currentBranchDetailsTemplate

        const currentBranchBrandingTemplate = {
            ...branchBrandingTemplate,
            logo: { url: companyDetails?.data.logo ? companyDetails?.data.logo : '', data: null, isChanged: false },
            picture: { url: companyDetails?.data.picture ? companyDetails?.data.picture : '', data: null, isChanged: false }
        }
        setBranchBrandingTemplate(currentBranchBrandingTemplate);
        branchLocationsRef.current['branchBrandingTemplate'] = currentBranchBrandingTemplate;

        // adding the same s3 filepath in a new key "filepath" because in out fileUploader
        // the uploading logic is handled by the filepath key (refer line 66 in SelectedFiles.tsx in Pixel)
        // adding another key 'size' to make the key names uniform with those of the FileUploader
        const formattedDocumentData = companyDetails?.data.quote?.attachments?.documents.map((el: IQuoteAttachmentDocumentsPreset) => ({ ...el, filepath: el.s3_url, size: el.size_in_byte }));

        const currentQuoteDetailTemplate = {
            ...quoteTemplate, quote_header_footer: {
                header_footer_section: companyDetails?.data.quote.quote_header_footer.header_footer_section,
                job_name: companyDetails?.data.quote.quote_header_footer.job_name,
                addressed_to: companyDetails?.data.quote.quote_header_footer.addressed_to,
                project_location: companyDetails?.data.quote.quote_header_footer.project_location,
                contact: companyDetails?.data.quote.quote_header_footer.contact,
                quote_number: companyDetails?.data.quote.quote_header_footer.quote_number,
                bid_date: companyDetails?.data.quote.quote_header_footer.bid_date,
                expiration_date: companyDetails?.data.quote.quote_header_footer.expiration_date,
                left_footer: companyDetails?.data.quote.quote_header_footer.left_footer,
                right_footer: companyDetails?.data.quote.quote_header_footer.right_footer,
                date: companyDetails?.data.quote.quote_header_footer.date,
                page_number: companyDetails?.data.quote.quote_header_footer.page_number,
                hyperlink: companyDetails?.data.quote.quote_header_footer.hyperlink,
                pre_fill_job_name: companyDetails?.data.quote.quote_header_footer.pre_fill_job_name,
                pre_fill_quote_number: companyDetails?.data.quote.quote_header_footer.pre_fill_quote_number,
                pre_fill_bid_date: companyDetails?.data.quote.quote_header_footer.pre_fill_bid_date,
                pre_fill_expiration_date: companyDetails?.data.quote.quote_header_footer.pre_fill_expiration_date,
                pre_fill_header_logo: companyDetails?.data.quote.quote_header_footer.pre_fill_header_logo,
                pre_fill_addressed_to: companyDetails?.data.quote.quote_header_footer.pre_fill_addressed_to
            },
            quote_cover_page: {
                cover_page_section: companyDetails?.data.quote.quote_cover_page.cover_page_section,
                stakeholders: companyDetails?.data.quote.quote_cover_page.stakeholders,
                prepared_by: companyDetails?.data.quote.quote_cover_page.prepared_by,
                addressed_to: companyDetails?.data.quote.quote_cover_page.addressed_to,
                opening_notes: companyDetails?.data.quote.quote_cover_page.opening_notes,
                opening_notes_text: {
                    value: companyDetails?.data.quote.quote_cover_page.opening_notes_text?.value,
                    font: { color: companyDetails?.data.quote.quote_cover_page.opening_notes_text?.font?.color, type: companyDetails?.data.quote.quote_cover_page.opening_notes_text?.font?.type }
                },
                pre_fill_preparedby_logo: companyDetails?.data.quote.quote_cover_page.pre_fill_preparedby_logo,
                pre_fill_addressed_to: companyDetails?.data.quote.quote_cover_page.pre_fill_addressed_to
            },
            quote_price_lead_time: {
                price_and_lead_time_section: companyDetails?.data.quote.quote_price_lead_time.price_and_lead_time_section,
                quantity: companyDetails?.data.quote.quote_price_lead_time.quantity,
                type: companyDetails?.data.quote.quote_price_lead_time.type,
                manufacturer: companyDetails?.data.quote.quote_price_lead_time.manufacturer,
                model_number: companyDetails?.data.quote.quote_price_lead_time.model_number,
                notes: companyDetails?.data.quote.quote_price_lead_time.notes,
                lead_time: companyDetails?.data.quote.quote_price_lead_time.lead_time,
                unit_price: companyDetails?.data.quote.quote_price_lead_time.unit_price,
                extended_price: companyDetails?.data.quote.quote_price_lead_time.extended_price,
                section_subtotal: companyDetails?.data.quote.quote_price_lead_time.section_subtotal,
                datasheet_hyperlink: companyDetails?.data.quote.quote_price_lead_time.datasheet_hyperlink,
                lot_breakout: companyDetails?.data.quote.quote_price_lead_time.lot_breakout,
                service_breakout: companyDetails?.data.quote.quote_price_lead_time.service_breakout,
                tax_breakout: companyDetails?.data.quote.quote_price_lead_time.tax_breakout,
                freight_breakout: companyDetails?.data.quote.quote_price_lead_time.freight_breakout,
                summarized_total: companyDetails?.data.quote.quote_price_lead_time.summarized_total,
                show_kit_breakdown: companyDetails?.data.quote.quote_price_lead_time.show_kit_breakdown,
                description: companyDetails?.data.quote.quote_price_lead_time.description
            },
            quote_general_tnc: {
                general_tnc_section: companyDetails?.data.quote.quote_general_tnc.general_tnc_section,
                closing_notes: companyDetails?.data.quote.quote_general_tnc.closing_notes,
                closing_notes_text: {
                    value: companyDetails?.data.quote.quote_general_tnc.closing_notes_text?.value,
                    font: { color: companyDetails?.data.quote.quote_general_tnc.closing_notes_text?.font?.color, type: companyDetails?.data.quote.quote_general_tnc.closing_notes_text?.font?.type }
                },
                project_notes: companyDetails?.data.quote.quote_general_tnc.project_notes,
                project_notes_text: companyDetails?.data.quote.quote_general_tnc.project_notes_text,
                general_tnc: companyDetails?.data.quote.quote_general_tnc.general_tnc,
                general_tnc_text: companyDetails?.data.quote.quote_general_tnc.general_tnc_text,
                is_general_tnc_locked: companyDetails?.data.quote.quote_general_tnc.is_general_tnc_locked
            },
            quote_manufacturer_tnc: {
                manufacturer_tnc_section: companyDetails?.data.quote.quote_manufacturer_tnc.manufacturer_tnc_section
            },
            quote_price_lead_time_option: {
                quantity: companyDetails?.data.quote.quote_price_lead_time_option.quantity,
                type: companyDetails?.data.quote.quote_price_lead_time_option.type,
                manufacturer: companyDetails?.data.quote.quote_price_lead_time_option.manufacturer,
                model_number: companyDetails?.data.quote.quote_price_lead_time_option.model_number,
                notes: companyDetails?.data.quote.quote_price_lead_time_option.notes,
                substituted_for: companyDetails?.data.quote.quote_price_lead_time_option.substituted_for,
                lead_time: companyDetails?.data.quote.quote_price_lead_time_option.lead_time,
                unit_price: companyDetails?.data.quote.quote_price_lead_time_option.unit_price,
                extended_price: companyDetails?.data.quote.quote_price_lead_time_option.extended_price,
                add_deduct_amount: companyDetails?.data.quote.quote_price_lead_time_option.add_deduct_amount,
                section_subtotal: companyDetails?.data.quote.quote_price_lead_time_option.section_subtotal,
                datasheet_hyperlink: companyDetails?.data.quote.quote_price_lead_time_option.datasheet_hyperlink,
                show_kit_breakdown: companyDetails?.data.quote.quote_price_lead_time_option.show_kit_breakdown,
                option_notes: companyDetails?.data.quote.quote_price_lead_time_option.option_notes,
                option_notes_text: {
                    value:
                        companyDetails?.data.quote.quote_price_lead_time_option.option_notes_text?.value === null
                            ? ''
                            : companyDetails?.data.quote.quote_price_lead_time_option.option_notes_text?.value,
                    font: {
                        color: companyDetails?.data.quote.quote_price_lead_time_option.option_notes_text?.font?.color,
                        type: companyDetails?.data.quote.quote_price_lead_time_option.option_notes_text?.font?.type
                    }
                },
                description: companyDetails?.data.quote.quote_price_lead_time_option.description
            },
            attachments: { is_enable: companyDetails?.data.quote?.attachments?.is_enable, company: locationId ? Number(locationId) : null, documents: formattedDocumentData }
        }
        setQuoteTemplate(currentQuoteDetailTemplate);
        branchLocationsRef.current['quoteTemplate'] = currentQuoteDetailTemplate;

        const currentSubmittalTemplate = {
            ...submittalTemplate,
            cover_page_template_id: companyDetails?.data.submittal.cover_page_template_id,
            cover_page_presets: {
                ...submittalTemplate.cover_page_presets,
                cover_page_section: companyDetails?.data.submittal.cover_page_presets.cover_page_section,
                date: companyDetails?.data.submittal.cover_page_presets.date,
                primary_title: companyDetails?.data.submittal.cover_page_presets.primary_title,
                secondary_title: companyDetails?.data.submittal.cover_page_presets.secondary_title,
                project_number: companyDetails?.data.submittal.cover_page_presets.project_number,
                quote_number: companyDetails?.data.submittal.cover_page_presets.quote_number,
                project_address: companyDetails?.data.submittal.cover_page_presets.project_address,
                stakeholders: companyDetails?.data.submittal.cover_page_presets.stakeholders,
                prepared_by: companyDetails?.data.submittal.cover_page_presets.prepared_by,
                addressed_to: companyDetails?.data.submittal.cover_page_presets.addressed_to,
                notes: companyDetails?.data.submittal.cover_page_presets.notes,
                pre_fill_primary_title: companyDetails?.data.submittal.cover_page_presets.pre_fill_primary_title,
                pre_fill_secondary_title: companyDetails?.data.submittal.cover_page_presets.pre_fill_secondary_title,
                pre_fill_date: companyDetails?.data.submittal.cover_page_presets.pre_fill_date,
                pre_fill_preparedby_logo: companyDetails?.data.submittal.cover_page_presets.pre_fill_preparedby_logo,
                pre_fill_coverpage_logo: companyDetails?.data.submittal.cover_page_presets.pre_fill_coverpage_logo,
                pre_fill_addressed_to: companyDetails?.data.submittal.cover_page_presets.pre_fill_addressed_to
            },
            header_footer_presets: {
                ...submittalTemplate.header_footer_presets,
                header_footer_section: companyDetails?.data.submittal.header_footer_presets.header_footer_section,
                job_name: companyDetails?.data.submittal.header_footer_presets.job_name,
                notes: companyDetails?.data.submittal.header_footer_presets.notes,
                document_type: companyDetails?.data.submittal.header_footer_presets.document_type,
                qty: companyDetails?.data.submittal.header_footer_presets.qty,
                left_footer: companyDetails?.data.submittal.header_footer_presets.left_footer,
                right_footer: companyDetails?.data.submittal.header_footer_presets.right_footer,
                date: companyDetails?.data.submittal.header_footer_presets.date,
                page_number: companyDetails?.data.submittal.header_footer_presets.page_number,
                hyperlinks: companyDetails?.data.submittal.header_footer_presets.hyperlinks,
                category: companyDetails?.data.submittal.header_footer_presets.category,
                pre_fill_job_name: companyDetails?.data.submittal.header_footer_presets.pre_fill_job_name,
                pre_fill_header_logo: companyDetails?.data.submittal.header_footer_presets.pre_fill_header_logo
            },
            table_of_content_presets: {
                ...submittalTemplate.table_of_content_presets,
                toc_section: companyDetails?.data.submittal.table_of_content_presets.toc_section,
                primary_title: companyDetails?.data.submittal.table_of_content_presets.primary_title,
                secondary_title: companyDetails?.data.submittal.table_of_content_presets.secondary_title,
                project_number: companyDetails?.data.submittal.table_of_content_presets.project_number,
                quote_number: companyDetails?.data.submittal.table_of_content_presets.quote_number,
                hyperlinks: companyDetails?.data.submittal.table_of_content_presets.hyperlinks,
                type: companyDetails?.data.submittal.table_of_content_presets.type,
                qty: companyDetails?.data.submittal.table_of_content_presets.qty,
                page_number: companyDetails?.data.submittal.table_of_content_presets.page_number,
                section: companyDetails?.data.submittal.table_of_content_presets.section
            },
            notes_presets: { ...submittalTemplate.notes_presets, notes_section: companyDetails?.data.submittal.notes_presets.notes_section },
            layout_presets: {
                ...submittalTemplate.layout_presets,
                datasheet: companyDetails?.data.submittal.layout_presets.datasheet,
                installation_guide: companyDetails?.data.submittal.layout_presets.installation_guide,
                drawing: companyDetails?.data.submittal.layout_presets.drawing,
                warranty: companyDetails?.data.submittal.layout_presets.warranty,
                other_document: companyDetails?.data.submittal.layout_presets.other_document,
                section_breaks: companyDetails?.data.submittal.layout_presets.section_breaks,
                show_kit_components: companyDetails?.data.submittal.layout_presets.show_kit_components,
                maintain_aspect_ratio: companyDetails?.data.submittal.layout_presets.maintain_aspect_ratio
            }
        }
        setSubmittalTemplate(currentSubmittalTemplate);
        branchLocationsRef.current['submittalTemplate'] = currentSubmittalTemplate;

        const currentOmPackageTemplate = {
            ...omPackageTemplate,
            cover_page_template_id: companyDetails?.data.o_m.cover_page_template_id,
            cover_page_presets: {
                ...omPackageTemplate.cover_page_presets,
                cover_page_section: companyDetails?.data.o_m.cover_page_presets.cover_page_section,
                date: companyDetails?.data.o_m.cover_page_presets.date,
                primary_title: companyDetails?.data.o_m.cover_page_presets.primary_title,
                secondary_title: companyDetails?.data.o_m.cover_page_presets.secondary_title,
                project_number: companyDetails?.data.o_m.cover_page_presets.project_number,
                quote_number: companyDetails?.data.o_m.cover_page_presets.quote_number,
                project_address: companyDetails?.data.o_m.cover_page_presets.project_address,
                stakeholders: companyDetails?.data.o_m.cover_page_presets.stakeholders,
                prepared_by: companyDetails?.data.o_m.cover_page_presets.prepared_by,
                addressed_to: companyDetails?.data.o_m.cover_page_presets.addressed_to,
                notes: companyDetails?.data.o_m.cover_page_presets.notes ? companyDetails?.data.o_m.cover_page_presets.notes : '',
                pre_fill_primary_title: companyDetails?.data.o_m.cover_page_presets.pre_fill_primary_title,
                pre_fill_secondary_title: companyDetails?.data.o_m.cover_page_presets.pre_fill_secondary_title,
                pre_fill_date: companyDetails?.data.o_m.cover_page_presets.pre_fill_date,
                pre_fill_preparedby_logo: companyDetails?.data.o_m.cover_page_presets.pre_fill_preparedby_logo,
                pre_fill_coverpage_logo: companyDetails?.data.o_m.cover_page_presets.pre_fill_coverpage_logo,
                pre_fill_addressed_to: companyDetails?.data.o_m.cover_page_presets.pre_fill_addressed_to
            },
            header_footer_presets: {
                ...omPackageTemplate.header_footer_presets,
                header_footer_section: companyDetails?.data.o_m.header_footer_presets.header_footer_section,
                job_name: companyDetails?.data.o_m.header_footer_presets.job_name,
                notes: companyDetails?.data.o_m.header_footer_presets.notes,
                document_type: companyDetails?.data.o_m.header_footer_presets.document_type,
                qty: companyDetails?.data.o_m.header_footer_presets.qty,
                left_footer: companyDetails?.data.o_m.header_footer_presets.left_footer,
                right_footer: companyDetails?.data.o_m.header_footer_presets.right_footer,
                date: companyDetails?.data.o_m.header_footer_presets.date,
                page_number: companyDetails?.data.o_m.header_footer_presets.page_number,
                hyperlinks: companyDetails?.data.o_m.header_footer_presets.hyperlinks,
                category: companyDetails?.data.o_m.header_footer_presets.category,
                pre_fill_job_name: companyDetails?.data.o_m.header_footer_presets.pre_fill_job_name,
                pre_fill_header_logo: companyDetails?.data.o_m.header_footer_presets.pre_fill_header_logo
            },
            table_of_content_presets: {
                ...omPackageTemplate.table_of_content_presets,
                toc_section: companyDetails?.data.o_m.table_of_content_presets.toc_section,
                primary_title: companyDetails?.data.o_m.table_of_content_presets.primary_title,
                secondary_title: companyDetails?.data.o_m.table_of_content_presets.secondary_title,
                project_number: companyDetails?.data.o_m.table_of_content_presets.project_number,
                quote_number: companyDetails?.data.o_m.table_of_content_presets.quote_number,
                hyperlinks: companyDetails?.data.o_m.table_of_content_presets.hyperlinks,
                type: companyDetails?.data.o_m.table_of_content_presets.type,
                qty: companyDetails?.data.o_m.table_of_content_presets.qty,
                page_number: companyDetails?.data.o_m.table_of_content_presets.page_number,
                section: companyDetails?.data.o_m.table_of_content_presets.section
            },
            notes_presets: { ...omPackageTemplate.notes_presets, notes_section: companyDetails?.data.o_m.notes_presets.notes_section },
            layout_presets: {
                ...omPackageTemplate.layout_presets,
                datasheet: companyDetails?.data.o_m.layout_presets.datasheet,
                installation_guide: companyDetails?.data.o_m.layout_presets.installation_guide,
                drawing: companyDetails?.data.o_m.layout_presets.drawing,
                warranty: companyDetails?.data.o_m.layout_presets.warranty,
                other_document: companyDetails?.data.o_m.layout_presets.other_document,
                section_breaks: companyDetails?.data.o_m.layout_presets.section_breaks,
                show_kit_components: companyDetails?.data.o_m.layout_presets.show_kit_components,
                maintain_aspect_ratio: companyDetails?.data.o_m.layout_presets.maintain_aspect_ratio
            }
        }
        setOmPackageTemplate(currentOmPackageTemplate);
        branchLocationsRef.current['omPackageTemplate'] = currentOmPackageTemplate;

        const currentBomSettingTemplate = {
            rounding_behaviour: companyDetails?.data.bom?.rounding_behaviour,
            rounding_precision: companyDetails?.data.bom?.rounding_precision
        }
        setBomSettingTemplate(currentBomSettingTemplate)
        branchLocationsRef.current['bomSettingTemplate'] = currentBomSettingTemplate;

    }, [companyDetails?.data]);

    const companyDetailsLoading = currentLocationId && Number(currentLocationId) !== 0 ? isLoading : false;

    const contextValue = {
        currentLocationId,
        isDuplicate,
        companyDetailsLoading,
        branchDetailsTemplate,
        setBranchDetailsTemplate,
        branchDetailsErrorState,
        setBranchDetailsErrorState,
        branchBrandingTemplate,
        setBranchBrandingTemplate,
        quoteTemplate,
        setQuoteTemplate,
        submittalTemplate,
        setSubmittalTemplate,
        omPackageTemplate,
        setOmPackageTemplate,
        bomSettingTemplate,
        setBomSettingTemplate,
        branchLocationsRef,
        companyDetails,
        companyData
    };

    return <AddEditLocationContext.Provider value={contextValue}>{children}</AddEditLocationContext.Provider>;
};

export const useLocationDetails = () => {
    return useContext(AddEditLocationContext);
};
